/* eslint-disable no-unused-vars */
import { useId, memo, useRef, useMemo } from 'react';
import { Search, Buttons } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import { useRerender } from 'utils';
import { Search as SmallSearchIcon, ColumnsIcon, BulkActions, ExportIcon } from 'assets/icons';
import { campaignReportsActions } from 'store/actions';
import { useSignal } from '@preact/signals-react';
import { DropDown } from './DropDown';
import * as S from './SearchCampaignReports.styled';
import { BulkActionsPanel } from './bulkActionsPanel/BulkActionsPanel';
import { campReportsSearchUISettings } from 'constants';
import { FiltersBtn } from './filtersBtn/FiltersBtn';
import { ColumnsBtn } from './columnsBtn/ColumnsBtn';
import { isAdminSelector } from 'store/selectors';

const btnCustomOpen = {
  color: 'rgb(59, 137, 246)', // primary_500
  marginRight: '8px',
};

export const SearchCampaignReports = memo(
  ({
    dateFrom$,
    dateTo$,
    isActive,
    toggle,
    toggleAllOff,
    handleExport,
    selectedAppsOrCountries$,
    isNoData$,
  }) => {
    const rerender = useRerender();
    const searchId = useId();
    const clearBtnId = useId();
    const exportBtnId = useId();
    const bulkActionsBtnId = useId();
    const currentRequestType = useSelector((state) => state.campaignReports.tableRequestTypes);
    const isPressedBulkBtn = useSelector((state) => state.campaignReports.isPressedBulkBtn);
    const updatedBidsArray = useSelector((state) => state.campaignReports.updatedBidsArray);
    const isAdmin = useSelector(isAdminSelector);

    const dispatch = useDispatch();

    const containerRef = useRef(null);
    const searchRef = useRef(null);
    const searchValue$ = useSignal('');

    const dropDownIsOpen$ = useSignal(false);

    const handleSearch = (event) => {
      searchValue$.value = event.target.value;
    };

    const handleClickToSearchBar = () => {
      dropDownIsOpen$.value = true;
    };

    const getSearchBarText = useMemo(() => {
      if (currentRequestType === 'Apps') return 'Search Application';
      if (currentRequestType === 'Country') return 'Search Country';
      return 'Search';
    }, [currentRequestType]);

    return (
      <div style={{ position: 'relative' }}>
        <S.Container>
          <S.SearchBox ref={containerRef} onClick={handleClickToSearchBar}>
            {campReportsSearchUISettings[currentRequestType].searchBox && (
              <Search
                onFocus={() => {}}
                ref={searchRef}
                id={searchId}
                onChange={handleSearch}
                value={searchValue$?.value}
                placeholder={getSearchBarText}
                rightIconOnClick={(event) => {
                  searchValue$.value = '';
                }}
                style={
                  dropDownIsOpen$.value === true
                    ? { zIndex: '900000', position: 'relative' }
                    : { position: 'relative' }
                }
                Icon={() => <SmallSearchIcon />}
              />
            )}
          </S.SearchBox>
          {dropDownIsOpen$.value && (
            <DropDown
              dropDownIsOpen$={dropDownIsOpen$}
              containerRef={containerRef}
              searchValue$={searchValue$}
              dateFrom$={dateFrom$}
              dateTo$={dateTo$}
              isActive={isActive}
              toggle={toggle}
              toggleAllOff={toggleAllOff}
              selectedAppsOrCountries$={selectedAppsOrCountries$}
            />
          )}
          {campReportsSearchUISettings[currentRequestType].searchBox && (
            <>
              <Buttons.QuaternaryNormal
                id={clearBtnId}
                isDisabled={!selectedAppsOrCountries$.value.size}
                isPressed={selectedAppsOrCountries$.value.size}
                style={selectedAppsOrCountries$.value.size ? btnCustomOpen : { marginRight: '8px' }}
                onClick={() => {
                  toggleAllOff();
                  rerender();
                }}
              >
                Clear All
              </Buttons.QuaternaryNormal>
              <VerticalLine style={{ margin: '0 16px' }} />
            </>
          )}
          {campReportsSearchUISettings[currentRequestType].filterBtn && <FiltersBtn />}

          {campReportsSearchUISettings[currentRequestType].bulkActionBtn && (
            <Buttons.QuaternaryNormal
              id={bulkActionsBtnId}
              icon={<BulkActions />}
              isDisabled={isNoData$.value}
              isPressed={isPressedBulkBtn}
              style={isPressedBulkBtn ? btnCustomOpen : { marginRight: '8px' }}
              onClick={() => {
                dispatch(
                  campaignReportsActions.updateProperty({
                    propField: 'isPressedBulkBtn',
                    newValue: true,
                  }),
                );
              }}
            >
              Bulk Actions
            </Buttons.QuaternaryNormal>
          )}
          {campReportsSearchUISettings[currentRequestType].exportBtn && (
            <Buttons.QuaternaryNormal
              id={exportBtnId}
              icon={<ExportIcon />}
              isDisabled={isNoData$.value}
              style={{ marginRight: '8px' }}
              onClick={() => handleExport(updatedBidsArray)}
            >
              Export
            </Buttons.QuaternaryNormal>
          )}
          {campReportsSearchUISettings[currentRequestType].columnsBtn && (
            <ColumnsBtn dateFrom$={dateFrom$} dateTo$={dateTo$} />
          )}
        </S.Container>
        <BulkActionsPanel isAdmin={isAdmin} />
      </div>
    );
  },
);

SearchCampaignReports.displayName = 'SearchCampaignReports';

const VerticalLine = (props) => (
  <svg
    style={{ ...props?.style }}
    width='1'
    height='32'
    viewBox='0 0 1 32'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect width='32' height='1' transform='matrix(0 -1 1 0 0 32)' fill='#E2E8F0' />
  </svg>
);
